import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { compileSystemPrompt, ScriptMode } from '@/lib/services/AgentScriptCompiler';
import { Loader, Save, RefreshCcw, FileText, Sparkles, Phone } from 'lucide-react';

type Company = {
  id: string;
  name: string;
  company_name?: string | null;
  system_prompt?: string | null;
  elevenlabs_agent_id?: string | null;
  is_active?: boolean;
};

export default function AgentScriptsCompilerPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [mode, setMode] = useState<ScriptMode>('inbound');
  const [compiledPrompt, setCompiledPrompt] = useState<string>('');
  const [knowledgeAppendix, setKnowledgeAppendix] = useState<string>('');
  const [isCompiling, setIsCompiling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toNumber, setToNumber] = useState<string>('');
  const [fromNumber, setFromNumber] = useState<string>('');
  const [overrideAgentId, setOverrideAgentId] = useState<string>('');
  const [isDialing, setIsDialing] = useState(false);

  async function loadCompanies() {
    try {
      setLoading(true);
      // First attempt: standard columns
      const { data, error } = await supabase
        .from('company_configs')
        .select('id, name, system_prompt, elevenlabs_agent_id, is_active')
        .eq('is_active', true)
        .order('name');

      let rows = data as any[] | null;

      // Fallback: some environments might not have "name" but have "company_name"
      if (error) {
        console.warn('Primary company query failed, attempting fallback alias to company_name', error);
        const { data: data2, error: error2 } = await supabase
          .from('company_configs')
          .select('id, name:company_name, system_prompt, elevenlabs_agent_id, is_active')
          .eq('is_active', true)
          .order('name');
        if (error2) throw error2;
        rows = (data2 as any[]) || [];
      }

      setCompanies(rows || []);
      if (rows && rows.length > 0) {
        setSelectedCompanyId(rows[0].id);
      }
    } catch (e) {
      console.error('Failed to load companies', e);
      const msg = (e as any)?.message || 'Unknown error';
      alert(`Failed to load companies: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCompanies();
  }, []);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId),
    [companies, selectedCompanyId]
  );

  const handleCompile = async () => {
    if (!selectedCompany) {
      alert('Select a company first');
      return;
    }
    try {
      setIsCompiling(true);
      const companyName = selectedCompany.company_name || selectedCompany.name || 'Right Away Home Services';
      const { systemPrompt, knowledgeAppendix } = compileSystemPrompt({ mode, companyName });
      setCompiledPrompt(systemPrompt);
      setKnowledgeAppendix(knowledgeAppendix);
    } catch (e) {
      console.error('Compile error', e);
      alert('Failed to compile scripts. See console for details.');
    } finally {
      setIsCompiling(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedCompany) return;
    if (!compiledPrompt.trim()) {
      alert('Compile a system prompt first.');
      return;
    }
    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('company_configs')
        .update({ system_prompt: compiledPrompt, updated_at: new Date().toISOString() })
        .eq('id', selectedCompany.id);
      if (error) throw error;
      alert('Saved system prompt to company configuration.');
    } catch (e) {
      console.error('Save error', e);
      alert('Failed to save system prompt.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSync = async () => {
    if (!selectedCompany) return;
    if (!compiledPrompt.trim()) {
      alert('Compile a system prompt first.');
      return;
    }

    setIsSyncing(true);

    // 1) Save prompt to company config
    const { error: saveErr } = await supabase
      .from('company_configs')
      .update({ system_prompt: compiledPrompt, updated_at: new Date().toISOString() })
      .eq('id', selectedCompany.id);
    if (saveErr) {
      console.error('Save error', saveErr);
      alert('Failed to save system prompt.');
      setIsSyncing(false);
      return;
    }

    // 2) Trigger sync on agent manager
    const { data: syncData, error: syncErr } = await supabase.functions.invoke('elevenlabs-agent-manager', {
      body: {
        action: 'sync',
        company_id: selectedCompany.id,
        // optional: mode hint, if agent-manager chooses to tailor anything
        mode
      }
    });

    if (syncErr) {
      const msg =
        (syncErr as any)?.message ||
        (syncErr as any)?.error ||
        (typeof syncErr === 'string' ? syncErr : JSON.stringify(syncErr));
      console.error('Save+Sync error', syncErr);
      alert(`Sync failed: ${msg}`);
      setIsSyncing(false);
      return;
    }

    alert('Saved and synced to ElevenLabs successfully.');
    setIsSyncing(false);
  };

  const handleTestOutboundCall = async () => {
    if (!selectedCompany) {
      alert('Select a company first');
      return;
    }
    if (!toNumber.trim()) {
      alert('Enter a destination number in E.164 format, e.g. +15551234567');
      return;
    }
    try {
      setIsDialing(true);
      const body: any = {
        toNumber: toNumber.trim(),
        company_id: selectedCompany.id
      };
      if (fromNumber.trim()) {
        body.fromNumber = fromNumber.trim();
      }
      if (overrideAgentId.trim()) {
        body.eleven_agent_id = overrideAgentId.trim();
      }
      const { data, error } = await supabase.functions.invoke('outbound-call', {
        body
      });
      if (error) {
        const msg =
          (error as any)?.message ||
          (error as any)?.error ||
          (typeof error === 'string' ? error : JSON.stringify(error));
        throw new Error(msg);
      }
      alert(`Outbound call initiated. CallSid: ${(data as any)?.callSid || 'unknown'}`);
    } catch (e) {
      console.error('Outbound call error', e);
      const msg = (e as any)?.message || 'Unknown error';
      alert(`Failed to start outbound call: ${msg}`);
    } finally {
      setIsDialing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader className="h-4 w-4 animate-spin" />
          <span>Loading companies…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Agent Scripts Compiler</h1>
          <p className="text-sm text-gray-600">
            Compile inbound/outbound script books into a single system prompt and sync to ElevenLabs.
          </p>
        </div>
      </header>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
        <p className="text-sm">
          About this page: Compile final agent system prompts from canonical sources
          <span className="font-mono"> inbound.md</span> and <span className="font-mono"> outbound.md</span>.
          Save writes to <span className="font-mono">company_configs.system_prompt</span>; Save & Sync also invokes
          <span className="font-mono"> elevenlabs-agent-manager</span> (action=sync) to update the live ElevenLabs agent.
        </p>
        <p className="text-xs mt-1">
          Tip: Seed knowledge/scripts via the seeding utility first (inbound-scripts-knowledge.md / outbound-scripts.md), then compile here to include the latest updates.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Company selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Company</label>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.is_active === false ? '(inactive)' : ''} {c.elevenlabs_agent_id ? '' : '— no agent'}
              </option>
            ))}
          </select>
          {selectedCompany?.elevenlabs_agent_id ? (
            <p className="text-xs text-green-700">Agent: {selectedCompany.elevenlabs_agent_id}</p>
          ) : (
            <p className="text-xs text-yellow-700">No ElevenLabs agent ID configured</p>
          )}
        </div>

        {/* Mode */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Script Mode</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as ScriptMode)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="inbound">Inbound (inbound.md)</option>
            <option value="outbound">Outbound (outbound.md)</option>
          </select>
          <p className="text-xs text-gray-500">
            Inbound uses inbound.md; Outbound uses outbound.md for compilation.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Actions</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCompile}
              disabled={isCompiling}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              title="Compile from inbound.md/outbound.md"
            >
              {isCompiling ? <Loader className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Compile
            </button>
            <button
              onClick={handleSavePrompt}
              disabled={isSaving || !compiledPrompt}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              title="Save compiled prompt to company_configs.system_prompt"
            >
              {isSaving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Prompt
            </button>
            <button
              onClick={handleSaveAndSync}
              disabled={isSyncing || !compiledPrompt}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
              title="Save and sync to ElevenLabs via agent manager"
            >
              {isSyncing ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              Save & Sync Agent
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Save Prompt stores into company_configs.system_prompt. Save & Sync also invokes elevenlabs-agent-manager (action=sync).
          </p>

          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-sm font-medium text-emerald-900 mb-2">Outbound Test</div>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <input
                type="tel"
                value={toNumber}
                onChange={(e) => setToNumber(e.target.value)}
                placeholder="+15551234567"
                className="w-full md:w-60 rounded-lg border border-gray-300 px-3 py-2"
              />
              <input
                type="tel"
                value={fromNumber}
                onChange={(e) => setFromNumber(e.target.value)}
                placeholder="From number (optional, E.164)"
                className="w-full md:w-60 rounded-lg border border-gray-300 px-3 py-2"
              />
              <input
                type="text"
                value={overrideAgentId}
                onChange={(e) => setOverrideAgentId(e.target.value)}
                placeholder="Override Eleven Agent ID (optional)"
                className="w-full md:w-80 rounded-lg border border-gray-300 px-3 py-2"
              />
              <button
                onClick={handleTestOutboundCall}
                disabled={isDialing || !toNumber.trim() || !selectedCompanyId}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                title="Initiate an outbound call via Twilio that streams to the ElevenLabs gateway"
              >
                {isDialing ? <Loader className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                Test Outbound Call
              </button>
            </div>
            <p className="text-xs text-emerald-800 mt-2">
              Uses the outbound-call function. If no override agent is provided, the function resolves the agent by company configuration.
            </p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-700" />
            <h2 className="font-semibold">Compiled System Prompt</h2>
          </div>
          <textarea
            value={compiledPrompt}
            onChange={(e) => setCompiledPrompt(e.target.value)}
            placeholder="Compile to view the generated system prompt…"
            rows={24}
            className="w-full rounded-lg border border-gray-300 p-3 font-mono text-xs"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-700" />
            <h2 className="font-semibold">Knowledge Appendix (Full Script Book)</h2>
          </div>
          <textarea
            value={knowledgeAppendix}
            readOnly
            placeholder="Compile to view full knowledge appendix…"
            rows={24}
            className="w-full rounded-lg border border-gray-300 p-3 font-mono text-xs bg-gray-50"
          />
        </div>
      </section>

      <section>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-800 text-sm">
          <p className="font-semibold mb-1">Notes</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Inbound uses inbound.md; Outbound uses outbound.md. Keep those documents up to date with call flows, rules, and scripts.
            </li>
            <li>
              The compiled prompt embeds the Mandatory Call Flow & Rules and pricing guardrails, plus persistence guidance.
            </li>
            <li>
              The ElevenLabs agent manager will push the latest system_prompt and voice/ASR settings during sync.
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
